// §6.3 — the macOS window frame: **traffic lights left**, vibrancy, a 12px radius, no border.
//
// The drag and resize path is the kernel's (`kernel/wm/drag`), exactly as the other two frames' is:
// geometry is written to the DOM and committed once (§5.2). A skin decides what a frame *looks*
// like and never how a window moves.
//
// Nothing here imports another skin — it may not (§6.5) — and the resemblance to the Windows frame
// is because both go through the same kernel services, not because either borrowed from the other.
//
// The green button is where this skin stops resembling anything else: §5.4 asks the *policy* what
// it means, and D-032's answer is a dedicated Space rather than a maximize.
import { useCallback, useRef } from 'react';
import type { Edge, Win } from '@hangar/shared';
import { COPY } from '@/copy';
import { useKernel } from '@/kernel/react';
import { MOVE, RESIZE_EDGES, beginDrag, resizeFrom, type DragSession } from '@/kernel/wm/drag';
import { macosPolicy } from '../policy';

export function WindowFrame({ win }: { win: Win }) {
  const kernel = useKernel();
  const session = useRef<DragSession | null>(null);

  const startDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>, edge: Edge | null) => {
      if (event.button !== 0) return;
      const host = event.currentTarget.closest<HTMLElement>('.win-host');
      if (!host) return;

      const current = kernel.wm.get(win.id);
      if (!current) return;
      if (edge !== null && !current.resizable) return;
      // A window filling its own Space is not draggable: there is nowhere to drag it to.
      if (current.state === 'fullscreenSpace') return;

      kernel.wm.focus(win.id);
      event.preventDefault();

      const kind = edge === null ? MOVE : resizeFrom(edge);
      // §5.4 — only a move can snap; a resize is the user saying what size they want.
      if (edge === null) kernel.snap.begin(win.id);
      session.current = beginDrag({
        element: host,
        capture: event.currentTarget,
        start: current.rect,
        origin: { clientX: event.clientX, clientY: event.clientY },
        pointerId: event.pointerId,
        kind,
        min: { w: current.minW, h: current.minH },
        // §5.2 (D-438) — passed for a resize as well as a move. A move is held wholly inside the
        // work area; a resize only has its top and left edges held there, so a window can still be
        // made larger than the screen without its title bar ending up above it.
        bounds: kernel.workArea(),
        onCommit: (rect) => {
          session.current = null;
          if (edge !== null) {
            kernel.wm.resize(win.id, rect);
            return;
          }
          if (!kernel.snap.commit()) kernel.wm.setRect(win.id, rect);
        },
        onCancel: () => kernel.snap.cancel(),
      });
    },
    [kernel, win.id],
  );

  const onPointerMove = (event: React.PointerEvent) => {
    if (!session.current) return;
    session.current.pointerMove(event.nativeEvent);
    kernel.snap.update({ x: event.clientX, y: event.clientY });
  };
  const onPointerUp = (event: React.PointerEvent) => session.current?.pointerUp(event.nativeEvent);
  const onPointerCancel = () => {
    kernel.snap.cancel();
    session.current?.cancel();
  };

  /**
   * §5.4 / D-032 — the green button. It asks the policy what it means and gets
   * `'enterFullscreenSpace'`, which is §5.5's dedicated Space rather than a maximize.
   *
   * The *same* button is the way out, because on this platform it is: a window in a Space shows
   * the inward arrows and pressing them brings it home.
   */
  const onZoom = () => {
    const current = kernel.wm.get(win.id);
    if (!current) return;
    if (current.state === 'fullscreenSpace') {
      kernel.desktops.exitFullscreenSpace(win.id);
      return;
    }
    if (current.state === 'snapped' || current.state === 'maximized') {
      kernel.wm.restore(win.id);
      return;
    }
    if (macosPolicy.onMaximizeButton(current) === 'enterFullscreenSpace') {
      kernel.desktops.enterFullscreenSpace(win.id, kernel.workArea());
    } else {
      kernel.wm.maximize(win.id, kernel.workArea());
    }
  };

  const inSpace = win.state === 'fullscreenSpace';

  return (
    <div className="macos-frame" data-testid={`window-frame-${win.id}`}>
      <div
        className="macos-titlebar"
        data-testid={`titlebar-${win.id}`}
        onPointerDown={(e) => startDrag(e, null)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDoubleClick={onZoom}
      >
        {/*
          §6.3 — **traffic lights, left**, in close / minimize / zoom order. The order is the
          platform's and is not negotiable: a user who has ever used a Mac aims at the leftmost
          circle to close, and getting it wrong closes the wrong thing.
        */}
        <div className="macos-lights" data-testid={`window-controls-${win.id}`}>
          <button
            type="button"
            className="macos-light macos-light--close"
            aria-label={COPY.chrome.close}
            data-glyph="close"
            data-testid={`window-close-${win.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            // §18 F9 — the user asking is what makes this vetoable; `wm.close` is not.
            onClick={() => void kernel.requestClose(win.id)}
          />
          <button
            type="button"
            className="macos-light macos-light--minimize"
            aria-label={COPY.chrome.minimize}
            data-glyph="minimize"
            data-testid={`window-minimize-${win.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => kernel.wm.minimize(win.id)}
          />
          <button
            type="button"
            className="macos-light macos-light--zoom"
            aria-label={inSpace ? COPY.chrome.restore : COPY.chrome.maximize}
            data-glyph="zoom"
            data-in-space={String(inSpace)}
            data-testid={`window-maximize-${win.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onZoom}
          />
        </div>

        <span className="macos-title" title={win.title}>
          {win.title}
        </span>

        {win.dirty ? (
          <span className="macos-dirty" data-testid="window-dirty" aria-label={COPY.chrome.unsaved} />
        ) : null}
        {win.busy ? (
          <span className="macos-busy" data-testid="window-busy" aria-label={COPY.chrome.busy} />
        ) : null}
      </div>

      {/*
        §6.3 — **no in-window menu bar on this skin.** The focused window's published tree goes to
        the global bar at the top of the screen instead, from the same `kernel.menus` service.
      */}

      {win.resizable && win.state === 'normal' ? (
        <div className="win-resize-handles" aria-hidden>
          {RESIZE_EDGES.map((edge) => (
            <div
              key={edge}
              className="win-resize-handle"
              data-edge={edge}
              data-testid={`resize-${edge}-${win.id}`}
              onPointerDown={(e) => startDrag(e, edge)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
